import { Card } from "@/components/ui/card";
import { BookOpen, HelpCircle, Info, Database, ExternalLink, Phone, Globe, Heart, Mail } from "lucide-react";
import { Button } from "@/components/ui/button";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { useState } from "react";
import SocialButtons from "@/components/home/SocialButtons";
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from "@/components/ui/accordion";

const Resources = () => {
  const [showEmergencyAlert] = useState(true);

  const emergencyContacts = {
    australia: {
      emergency: "000",
      lifeline: "13 11 14",
      beyondBlue: "1300 22 4636",
      kidshelpline: "1800 55 1800",
    },
    usa: {
      emergency: "911",
      suicide: "988",
      crisis: "741741",
      veterans: "1-800-273-8255",
    }
  };

  return (
    <ScrollArea className="h-[calc(100vh-4rem)]">
      <div className="container mx-auto px-4 py-8">
        {showEmergencyAlert && (
          <Alert className="mb-8 border-destructive">
            <AlertDescription className="text-destructive font-semibold flex flex-col gap-2">
              <div>🇦🇺 Australia: Call 000 or Lifeline (13 11 14) for immediate crisis support</div>
              <div>🇺🇸 USA: Call 911 or 988 Suicide & Crisis Lifeline for immediate help</div>
            </AlertDescription>
          </Alert>
        )}

        <h1 className="text-4xl font-bold mb-8 gradient-text animate-fade-in">Mental Health Resources & Support</h1>
        
        <div className="grid gap-8">
          {/* Crisis Support Section */}
          <section className="animate-fade-in">
            <h2 className="text-2xl font-semibold mb-6 flex items-center gap-2 text-destructive">
              <Phone className="h-6 w-6" />
              24/7 Crisis Support
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {/* Australia Crisis Support */}
              <Card className="p-6 border-destructive">
                <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  🇦🇺 Australia
                </h3>
                <ul className="space-y-4">
                  <li className="flex items-center justify-between">
                    <span className="font-semibold">Emergency Services</span>
                    <Button variant="default" size="sm" asChild>
                      <a href="tel:000">Call 000</a>
                    </Button>
                  </li>
                  <li className="flex items-center justify-between">
                    <span className="font-semibold">Lifeline Australia</span>
                    <Button variant="default" size="sm" asChild>
                      <a href="tel:131114">13 11 14</a>
                    </Button>
                  </li>
                  <li className="flex items-center justify-between">
                    <span className="font-semibold">Beyond Blue</span>
                    <Button variant="default" size="sm" asChild>
                      <a href="tel:1300224636">1300 22 4636</a>
                    </Button>
                  </li>
                  <li className="flex items-center justify-between">
                    <span className="font-semibold">Kids Helpline</span>
                    <Button variant="default" size="sm" asChild>
                      <a href="tel:1800551800">1800 55 1800</a>
                    </Button>
                  </li>
                </ul>
              </Card>

              {/* USA Crisis Support */}
              <Card className="p-6 border-destructive">
                <h3 className="text-xl font-semibold mb-4 flex items-center gap-2">
                  🇺🇸 United States
                </h3>
                <ul className="space-y-4">
                  <li className="flex items-center justify-between">
                    <span className="font-semibold">Emergency Services</span>
                    <Button variant="default" size="sm" asChild>
                      <a href="tel:911">Call 911</a>
                    </Button>
                  </li>
                  <li className="flex items-center justify-between">
                    <span className="font-semibold">988 Lifeline</span>
                    <Button variant="default" size="sm" asChild>
                      <a href="tel:988">Call 988</a>
                    </Button>
                  </li>
                  <li className="flex items-center justify-between">
                    <span className="font-semibold">Crisis Text Line</span>
                    <Button variant="default" size="sm" onClick={() => navigator.clipboard.writeText('HOME')}>
                      Text HOME to 741741
                    </Button>
                  </li>
                  <li className="flex items-center justify-between">
                    <span className="font-semibold">Veterans Crisis Line</span>
                    <Button variant="default" size="sm" asChild>
                      <a href="tel:18002738255">1-800-273-8255</a>
                    </Button>
                  </li>
                </ul>
              </Card>
            </div>
          </section>

          {/* DBT Resources Section */}
          <section className="animate-fade-in delay-100">
            <h2 className="text-2xl font-semibold mb-6 flex items-center gap-2">
              <BookOpen className="h-6 w-6" />
              DBT Resources by Region
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {/* Australia DBT Resources */}
              <Card className="p-6">
                <h3 className="text-xl font-semibold mb-4">🇦🇺 Australian DBT Programs</h3>
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="item-1">
                    <AccordionTrigger>Find DBT Therapists</AccordionTrigger>
                    <AccordionContent>
                      <ul className="space-y-2">
                        <li>
                          <a href="https://www.psychology.org.au/Find-a-Psychologist" 
                             target="_blank" 
                             rel="noopener noreferrer"
                             className="flex items-center gap-2 text-primary hover:underline">
                            APS Find a Psychologist <ExternalLink className="h-4 w-4" />
                          </a>
                        </li>
                        <li>
                          <a href="https://www.ranzcp.org/find-a-psychiatrist" 
                             target="_blank" 
                             rel="noopener noreferrer"
                             className="flex items-center gap-2 text-primary hover:underline">
                            RANZCP Find a Psychiatrist <ExternalLink className="h-4 w-4" />
                          </a>
                        </li>
                      </ul>
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="item-2">
                    <AccordionTrigger>DBT Programs</AccordionTrigger>
                    <AccordionContent>
                      <ul className="space-y-2">
                        <li>
                          <a href="https://www.sane.org/services/online-programs" 
                             target="_blank" 
                             rel="noopener noreferrer"
                             className="flex items-center gap-2 text-primary hover:underline">
                            SANE Australia Programs <ExternalLink className="h-4 w-4" />
                          </a>
                        </li>
                        <li>
                          <a href="https://www.blackdoginstitute.org.au/resources-support/digital-tools-apps/" 
                             target="_blank" 
                             rel="noopener noreferrer"
                             className="flex items-center gap-2 text-primary hover:underline">
                            Black Dog Institute Resources <ExternalLink className="h-4 w-4" />
                          </a>
                        </li>
                      </ul>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </Card>

              {/* USA DBT Resources */}
              <Card className="p-6">
                <h3 className="text-xl font-semibold mb-4">🇺🇸 US DBT Programs</h3>
                <Accordion type="single" collapsible className="w-full">
                  <AccordionItem value="item-1">
                    <AccordionTrigger>Find DBT Therapists</AccordionTrigger>
                    <AccordionContent>
                      <ul className="space-y-2">
                        <li>
                          <a href="https://behavioraltech.org/resources/find-a-therapist/" 
                             target="_blank" 
                             rel="noopener noreferrer"
                             className="flex items-center gap-2 text-primary hover:underline">
                            Behavioral Tech Therapist Directory <ExternalLink className="h-4 w-4" />
                          </a>
                        </li>
                        <li>
                          <a href="https://www.psychologytoday.com/us/therapists/dialectical-dbt" 
                             target="_blank" 
                             rel="noopener noreferrer"
                             className="flex items-center gap-2 text-primary hover:underline">
                            Psychology Today DBT Directory <ExternalLink className="h-4 w-4" />
                          </a>
                        </li>
                      </ul>
                    </AccordionContent>
                  </AccordionItem>
                  <AccordionItem value="item-2">
                    <AccordionTrigger>DBT Programs</AccordionTrigger>
                    <AccordionContent>
                      <ul className="space-y-2">
                        <li>
                          <a href="https://www.nami.org/Support-Education/Support-Groups" 
                             target="_blank" 
                             rel="noopener noreferrer"
                             className="flex items-center gap-2 text-primary hover:underline">
                            NAMI Support Groups <ExternalLink className="h-4 w-4" />
                          </a>
                        </li>
                        <li>
                          <a href="https://www.dbtselfhelp.com/" 
                             target="_blank" 
                             rel="noopener noreferrer"
                             className="flex items-center gap-2 text-primary hover:underline">
                            DBT Self Help Resources <ExternalLink className="h-4 w-4" />
                          </a>
                        </li>
                      </ul>
                    </AccordionContent>
                  </AccordionItem>
                </Accordion>
              </Card>
            </div>
          </section>

          {/* Additional Support Section */}
          <section className="animate-fade-in delay-200">
            <h2 className="text-2xl font-semibold mb-6 flex items-center gap-2">
              <Heart className="h-6 w-6" />
              Additional Support Services
            </h2>
            <div className="grid md:grid-cols-2 gap-6">
              {/* Australia Additional Support */}
              <Card className="p-6">
                <h3 className="text-xl font-semibold mb-4">🇦🇺 Australian Services</h3>
                <ul className="space-y-3">
                  <li>
                    <a href="https://www.headspace.org.au/" 
                       target="_blank" 
                       rel="noopener noreferrer"
                       className="flex items-center gap-2 text-primary hover:underline">
                      Headspace (Youth Mental Health) <ExternalLink className="h-4 w-4" />
                    </a>
                  </li>
                  <li>
                    <a href="https://www.beyondblue.org.au/" 
                       target="_blank" 
                       rel="noopener noreferrer"
                       className="flex items-center gap-2 text-primary hover:underline">
                      Beyond Blue Resources <ExternalLink className="h-4 w-4" />
                    </a>
                  </li>
                  <li>
                    <a href="https://www.blackdoginstitute.org.au/" 
                       target="_blank" 
                       rel="noopener noreferrer"
                       className="flex items-center gap-2 text-primary hover:underline">
                      Black Dog Institute <ExternalLink className="h-4 w-4" />
                    </a>
                  </li>
                </ul>
              </Card>

              {/* USA Additional Support */}
              <Card className="p-6">
                <h3 className="text-xl font-semibold mb-4">🇺🇸 US Services</h3>
                <ul className="space-y-3">
                  <li>
                    <a href="https://www.samhsa.gov/find-treatment" 
                       target="_blank" 
                       rel="noopener noreferrer"
                       className="flex items-center gap-2 text-primary hover:underline">
                      SAMHSA Treatment Locator <ExternalLink className="h-4 w-4" />
                    </a>
                  </li>
                  <li>
                    <a href="https://www.nami.org/Home" 
                       target="_blank" 
                       rel="noopener noreferrer"
                       className="flex items-center gap-2 text-primary hover:underline">
                      NAMI Resources <ExternalLink className="h-4 w-4" />
                    </a>
                  </li>
                  <li>
                    <a href="https://www.mhanational.org/" 
                       target="_blank" 
                       rel="noopener noreferrer"
                       className="flex items-center gap-2 text-primary hover:underline">
                      Mental Health America <ExternalLink className="h-4 w-4" />
                    </a>
                  </li>
                </ul>
              </Card>
            </div>
          </section>

          {/* Contact Support */}
          <section className="animate-fade-in delay-300">
            <h2 className="text-2xl font-semibold mb-6 flex items-center gap-2">
              <Globe className="h-6 w-6" />
              Need More Help?
            </h2>
            <Card className="p-6">
              <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                <div className="flex items-center gap-2">
                  <Mail className="h-5 w-5 text-primary" />
                  <p>Contact our support team or reach out to a mental health professional in your region</p>
                </div>
                <Button className="w-full md:w-auto" asChild>
                  <a href="mailto:hello@dbtai.xyz">Contact Support</a>
                </Button>
              </div>
            </Card>
          </section>

          {/* Social Media Links */}
          <section className="animate-fade-in delay-400">
            <h2 className="text-2xl font-semibold mb-6 flex items-center gap-2">
              <Heart className="h-6 w-6" />
              Connect With Us
            </h2>
            <SocialButtons />
          </section>
        </div>
      </div>
    </ScrollArea>
  );
};

export default Resources;
